import type { AxiosError } from "axios";

import { DeleteIcon } from "@/components/atoms/icons";
import { DropdownAction } from "@/components/organisms/DropdownAction";
import { IMaterial } from "@/config/models/materials.types";
import { useNotificationNamek } from "@/features/notifications";
import { DeleteErrorMessage } from "@/constants/commonMessages";
import type { INamekErrorRaw } from "@/models";
import { useRefetchMaterialsList } from "@/config/hooks/useRefetchMaterialsList";

import { getGreenCentersMessage } from "../../utils/getGreenCentersMessage";
import { useDeleteMaterial } from "../../services/useDeleteMaterial";

type ActionDeleteProps = {
  material: IMaterial;
};

export function ActionDeleteItem({ material }: ActionDeleteProps) {
  const { confirm, toast } = useNotificationNamek();
  const { refetchList } = useRefetchMaterialsList();

  const deleter = useDeleteMaterial();

  const doDelete = (id: number) => {
    const promise = deleter.mutateAsync(id);
    promise
      .then(() => {
        toast.success("Material eliminado");
      })
      .catch((error) => {
        const axiosError = error as AxiosError<INamekErrorRaw>;
        const status = axiosError.response?.status;

        if (status === 404) {
          toast.error("No se pudo eliminar el material", {
            description: `El material ${material.name} no existe o fue eliminado del listado.`,
            actionButton: {
              label: "Actualizar listado",
              onClick: refetchList,
            },
          });
          return;
        } else if (status === 409) {
          const data = axiosError.response?.data;
          const centersMessage = getGreenCentersMessage(data);

          toast.error("No se pudo eliminar el material", {
            description: `${material.name} es el único material asociado a ${centersMessage}. Asigna otro material para eliminarlo.`,
          });
          return;
        }

        toast.error("No se pudo eliminar el material", {
          ...DeleteErrorMessage,
        });
      });

    return promise;
  };

  const onClick = () => {
    confirm.show({
      title: "Eliminar material",
      content: (
        <div className="max-w-[430px] text-left">
          <p className="font-normal">
            El material será removido de la lista y de los centros verdes que lo tengan asignado. No
            podrás crear materiales con el mismo nombre.
          </p>
          <p className="font-bold">¿Eliminar material?</p>
        </div>
      ),
      acceptLabel: "Eliminar",
      accept: () => {
        return doDelete(material.id)
          .then(() => true)
          .catch(() => true);
      },
    });
  };

  return <DropdownAction.Item icon={<DeleteIcon />} label="Eliminar" onClick={onClick} />;
}
